import axios from 'axios';
import { extractControllerFromUrl, extractFunnelPageIdFromUrl } from '../Util/URLBuilder';

/**
 * HookSurveys-specific form submission implementation
 * This replaces the checkForm function from page.php
 * 
 * @param {object} submitButton - The submit button element
 * @param {object} form - The form element
 * @param {object} options - Additional options like funnel_page_id, turnstileToken, etc.
 * @returns {Promise} - Promise that resolves with submission result
 */
export const submitForm = async (submitButton, form, options = {}) => {
    // Extract options with defaults to handle undefined values
    const { 
        funnel_page_id, 
        turnstileToken, 
        ajaxUrl 
    } = options;
    
    // Create a default t function if none is provided
    const defaultT = key => key; // Identity function that returns the key unchanged
    const t = typeof options.t === 'function' ? options.t : defaultT;
    const optionsOpenAlert = options.openAlert;
    
    // Use the translation function properly
    const translate = (key, defaultText) => {
        try {
            return t(key);
        } catch (e) {
            // If t throws an error, fall back to default text
            console.warn('Translation function error:', e);
            return defaultText || key;
        }
    };
    
    // Use a consistent alert function
    const showAlert = (message) => {
        try {
            // First try the options-provided openAlert
            if (typeof optionsOpenAlert === 'function') {
                optionsOpenAlert(message);
                return;
            }
            
            // Then try window.openAlert
            if (typeof window.openAlert === 'function') {
                // Pass the message directly without translation - 
                // window.openAlert is responsible for translation
                window.openAlert(message, t);
                return;
            }
            
            // Then try SweetAlert if available
            if (typeof Swal !== 'undefined') {
                // Use our translation helper with fallback
                const displayMessage = translate(message, message);
                Swal.fire({
                    title: '',
                    text: displayMessage,
                    icon: 'warning',
                    confirmButtonText: translate('OK', 'OK')
                });
                return;
            }
            
            // Fallback to browser alert if no alert function is available
            console.warn('No alert function available, using native alert:', message);
            alert(message);
        } catch (alertError) {
            console.error('Error showing alert:', alertError);
            // Final fallback if everything else fails
            try {
                alert(message);
            } catch (e) {
                console.error('Even native alert failed:', e);
            }
        }
    };
    
    try {
        // Show loading indicator
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'on');
            submitButton.disabled = true;
        }
        
        // Collect form data directly from form fields
        const formData = new FormData(form);

        // Ensure _csrfToken is included
        const csrfInput = document.querySelector('input[name="_csrfToken"]');
        let csrfToken = null;
        if (csrfInput && csrfInput.value && !formData.has('_csrfToken')) {
            formData.append('_csrfToken', csrfInput.value);
            csrfToken = csrfInput.value;
        } else if (csrfInput && csrfInput.value) {
            csrfToken = csrfInput.value;
        }

        const formDataStr = new URLSearchParams(formData).toString();
        
        // Ensure Turnstile token is included
        if (turnstileToken) {
            // Add the token to the request headers
            axios.defaults.headers.common['X-Turnstile-Token'] = turnstileToken;
        }
        
        // Always use the provided ajaxUrl directly if available
        // This ensures we use exactly the URL constructed by the button component
        const requestUrl = ajaxUrl || null;
        
        if (!requestUrl) {
            console.error('No AJAX URL provided for form submission');
            const errorMessage = translate('提交表單時發生錯誤，請重試。', '提交表單時發生錯誤，請重試。');
            showAlert(errorMessage);
            
            // Reset button state
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            
            // Create a proper error with the message
            const error = new Error(errorMessage);
            error.code = 'URL_MISSING';
            throw error;
        }
        
        console.log(`HookSurveys: Submitting to ${requestUrl}`);
        
        // Prepare headers
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
        };
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }
        
        const response = await axios.post(requestUrl, formDataStr, {
            headers: headers
        });
        
        const data = response.data;
        if (data.require_turnstile) {
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const errorMessage = data.rm || 'Security verification required. Please try again.';
            showAlert(errorMessage);
            return { success: false, require_turnstile: true, message: errorMessage };
        }
        if (data.rc === 1 || data.rc === true) {
            // Handle redirect if present
            if (data.redirect_url || data.redirect) {
                const redirectUrl = data.redirect_url || data.redirect;
                window.location.href = redirectUrl;
                return { success: true, redirected: true };
            }
            
            // Process HTML content replacement if present
            if (data.html_content) {
                const formContainer = form.parentElement;
                if (formContainer) {
                    formContainer.innerHTML = data.html_content;
                }
                return { success: true, replaced: true };
            }
            
            // Standard form submission
            if (form.getAttribute('action')) {
				const inIframe = (() => {
					try {
						return window.self !== window.top;
					} catch (e) {
						return true;
					}
				})();

				// In iframe mode, avoid native form.submit() so we can attach X-Iframe header.
				if (inIframe) {
					const actionUrl = form.getAttribute('action');
					headers['X-Iframe'] = '1';
					const resp = await axios.post(actionUrl, formDataStr, { headers: headers });
					const finalUrl = resp?.request?.responseURL;
					window.location.href = finalUrl || actionUrl;
					return { success: true, submitted: true, redirected: true };
				}

                form.submit();
                return { success: true, submitted: true };
            }
            
            return { success: true };
        } else {
            // Form validation failed
            console.log('HookSurveys: Form validation failed', data.rm);
            
            // Reset button state
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            
            // Check if server returned updated HTML content (e.g., with error messages)
            if (data.html_content) {
                console.log('HookSurveys: Server returned updated HTML content for validation failure');
                const formContainer = form.parentElement;
                if (formContainer) {
                    formContainer.innerHTML = data.html_content;
                }
                return { success: false, replaced: true };
            }
            
            // Get the exact error message from the server response
            let errorMessage = '表單驗證失敗';
            if (data.rm) {
                errorMessage = data.rm; // Use the exact message from the server
                console.log('Server validation error message:', errorMessage);
            }
            
            // Show error message directly in alert without translation
            showAlert(errorMessage);
            
            // Return error info
            return { success: false, message: errorMessage };
        }
    } catch (error) {
        console.error('HookSurveys: Form submission error', error);
        
        // Reset button state
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'off');
            submitButton.disabled = false;
        }
        
        // Show error message
        const errorMessage = translate('系統發生錯誤，請稍後再試。', '系統發生錯誤，請稍後再試。');
        
        showAlert(errorMessage);
        
        throw error; // Re-throw to ensure NonSE.js catches it for proper display
    }
};

export default {
    submitForm
};
